import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import logo from "../Images/para.png";
import { getExpenseDetails } from "../../../Services/Services";

pdfMake.vfs = pdfFonts.vfs;

// ──────────────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────────────
const formatDateLong = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = months[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatCurrency = (amount) => {
  const val = Number(amount) || 0;
  return val.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

// Expense type label mapping
const EXPENSE_TYPE_LABELS = {
  diesel: "Diesel",
  tyre: "Tyre",
  repair: "Repair",
  service: "Service",
  rto: "RTO",
  insurance: "Insurance",
  tax: "Tax",
  toll_tax: "Toll Tax",
  food: "Food",
  other: "Other",
};

const getExpenseTypeLabel = (type) =>
  EXPENSE_TYPE_LABELS[type] || (type ? type.toUpperCase() : "-");

// ──────────────────────────────────────────────────────────────────────────
// SINGLE VOUCHER PDF GENERATOR
// ──────────────────────────────────────────────────────────────────────────
export const generateSingleExpensePDF = (voucher) => {
  const lineItems = voucher.line_items || [];
  const ownerCompanyName = "PARAS ROADLINES";
  const HEADER_BG = "#e8e8e8";

  const vehicleNumber = voucher.vehicle_number || "-";
  const expenseDate = formatDateLong(voucher.expense_date);
  const voucherId = voucher.expense_id || "-";
  const remarks = voucher.remarks || "-";
  const createdAt = voucher.created_at;

  // ═══════════════════════════════════════════════════════
  // BUILD EXPENSE TABLE BODY
  // Columns: Srl | Expense Type | Vendor | Description | Payment Mode | Receipt No | KM | Amount
  // ═══════════════════════════════════════════════════════
  const expenseTableBody = [];

  // Header
  expenseTableBody.push([
    {
      text: "Srl",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "Expense Type",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "Vendor",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "Description",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "Payment Mode",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "Receipt No.",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "KM Reading",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
    {
      text: "Amount (Rs.)",
      bold: true,
      fontSize: 9,
      alignment: "center",
      fillColor: HEADER_BG,
      margin: [0, 4, 0, 4],
    },
  ]);

  // Data rows
  let totalAmount = 0;
  if (lineItems.length > 0) {
    lineItems.forEach((item, idx) => {
      const amt = parseFloat(item.amount) || 0;
      totalAmount += amt;

      expenseTableBody.push([
        {
          text: (idx + 1).toString(),
          fontSize: 8,
          alignment: "center",
          margin: [0, 3, 0, 3],
        },
        {
          text: getExpenseTypeLabel(item.expense_type),
          fontSize: 8,
          alignment: "center",
          bold: true,
          margin: [2, 3, 2, 3],
        },
        {
          text: item.vendor_name || "-",
          fontSize: 8,
          alignment: "left",
          margin: [3, 3, 3, 3],
        },
        {
          text: item.description || "-",
          fontSize: 8,
          alignment: "left",
          margin: [3, 3, 3, 3],
        },
        {
          text: item.payment_mode || "-",
          fontSize: 8,
          alignment: "center",
          margin: [2, 3, 2, 3],
        },
        {
          text: item.receipt_no || "-",
          fontSize: 8,
          alignment: "center",
          margin: [2, 3, 2, 3],
        },
        {
          text: item.km_reading ? `${item.km_reading}` : "-",
          fontSize: 8,
          alignment: "center",
          margin: [2, 3, 2, 3],
        },
        {
          text: formatCurrency(amt),
          fontSize: 8,
          alignment: "right",
          margin: [0, 3, 3, 3],
        },
      ]);
    });
  } else {
    expenseTableBody.push([
      {
        text: "No expense line items found",
        colSpan: 8,
        fontSize: 9,
        alignment: "center",
        italics: true,
        color: "#666666",
        margin: [0, 8, 0, 8],
      },
      {}, {}, {}, {}, {}, {}, {},
    ]);
  }

  // GRAND TOTAL ROW
  const grandTotal = parseFloat(voucher.total_amount) || totalAmount;

  expenseTableBody.push([
    {
      text: "GRAND TOTAL",
      bold: true,
      fontSize: 10,
      alignment: "right",
      colSpan: 7,
      fillColor: "#d9d9d9",
      margin: [0, 4, 4, 4],
    },
    {}, {}, {}, {}, {}, {},
    {
      text: formatCurrency(grandTotal),
      bold: true,
      fontSize: 10,
      alignment: "right",
      fillColor: "#d9d9d9",
      margin: [0, 4, 3, 4],
    },
  ]);

  // ── DOCUMENT DEFINITION ──────────────────────────────────
  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [15, 15, 15, 30],
    defaultStyle: {
      fontSize: 10,
      font: "Roboto",
      color: "#000000",
    },
    images: { logo: logo },
    footer: function (currentPage, pageCount) {
      return {
        text: `This electronic generated pdf does not require any physical signature. Date : ${formatDateTime(createdAt)}`,
        fontSize: 8,
        alignment: "left",
        margin: [15, 5, 15, 0],
      };
    },

    content: [
      // ═══════════════════════════════════════════════════════
      // TITLE
      // ═══════════════════════════════════════════════════════
      {
        text: "VEHICLE EXPENSE VOUCHER",
        bold: true,
        fontSize: 14,
        alignment: "center",
        margin: [0, 0, 0, 6],
      },

      // ═══════════════════════════════════════════════════════
      // HEADER SECTION
      // ═══════════════════════════════════════════════════════
      {
        columns: [
          {
            width: 90,
            stack: [
              {
                image: "logo",
                width: 70,
                height: 70,
                margin: [0, 5, 0, 0],
              },
            ],
          },
          {
            width: "*",
            stack: [
              {
                text: "Subject to BHILAI jurisdiction",
                fontSize: 9,
                alignment: "right",
                margin: [0, 0, 0, 2],
              },
              {
                text: ownerCompanyName,
                bold: true,
                fontSize: 18,
                alignment: "right",
                color: "#c00000",
                margin: [0, 0, 0, 2],
              },
              {
                text: "(FLEET OWNER & TRANSPORTING CONTRACTORS)",
                bold: true,
                fontSize: 10,
                alignment: "right",
                margin: [0, 0, 0, 2],
              },
              {
                text: "52, R.S.S. Market, Power House, Bhilai - 490011 (C.G.)",
                fontSize: 9,
                alignment: "right",
                margin: [0, 0, 0, 4],
              },
              {
                text: [
                  { text: "MOB", bold: true, fontSize: 9 },
                  {
                    text: " : 7974125386, 7999552398, 9425234581",
                    fontSize: 9,
                  },
                ],
                alignment: "right",
                margin: [0, 0, 0, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 10],
      },

      // ═══════════════════════════════════════════════════════
      // GSTIN & DAILY SERVICE LINE
      // ═══════════════════════════════════════════════════════
      {
        stack: [
          {
            text: [
              { text: "GSTIN NO. : ", bold: true, fontSize: 9 },
              { text: "27AAICV3639B12C", fontSize: 9 },
            ],
          },
          {
            text: [
              { text: "DAILY SERVICE : ", bold: true, fontSize: 9 },
              { text: "ALL OVER INDIA", fontSize: 9 },
            ],
          },
        ],
        margin: [0, 4, 0, 4],
      },

      // Horizontal line
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 0,
            x2: 565,
            y2: 0,
            lineWidth: 1,
            lineColor: "#000000",
          },
        ],
        margin: [0, 0, 0, 6],
      },

      // ═══════════════════════════════════════════════════════
      // VOUCHER NO AND DATE ROW
      // ═══════════════════════════════════════════════════════
      {
        columns: [
          {
            width: "*",
            text: [
              { text: "Voucher No. : ", fontSize: 11 },
              {
                text: `PRL/EXP/${voucherId}`,
                bold: true,
                fontSize: 12,
              },
            ],
          },
          {
            width: "auto",
            text: [
              { text: "Date : ", bold: true, fontSize: 11 },
              { text: expenseDate, bold: true, fontSize: 11 },
            ],
            alignment: "right",
          },
        ],
        margin: [0, 0, 0, 6],
      },

      // ═══════════════════════════════════════════════════════
      // VEHICLE DETAILS SECTION
      // ═══════════════════════════════════════════════════════
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "VEHICLE DETAILS",
                bold: true,
                fontSize: 11,
                fillColor: HEADER_BG,
                margin: [5, 3, 5, 3],
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
        },
      },
      {
        table: {
          widths: ["*", "*", "*"],
          body: [
            [
              {
                text: "VEHICLE NUMBER",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 2],
              },
              {
                text: "EXPENSE DATE",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 2],
              },
              {
                text: "TOTAL EXPENSES",
                bold: true,
                fontSize: 9,
                alignment: "center",
                margin: [2, 5, 2, 2],
              },
            ],
            [
              {
                text: vehicleNumber,
                fontSize: 11,
                bold: true,
                alignment: "center",
                color: "#c00000",
                margin: [2, 4, 2, 6],
              },
              {
                text: expenseDate,
                fontSize: 10,
                bold: true,
                alignment: "center",
                margin: [2, 4, 2, 6],
              },
              {
                text: `${lineItems.length} Item${lineItems.length === 1 ? "" : "s"}`,
                fontSize: 10,
                bold: true,
                alignment: "center",
                margin: [2, 4, 2, 6],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 8],
      },

      // ═══════════════════════════════════════════════════════
      // EXPENSE LINE ITEMS TABLE HEADER
      // ═══════════════════════════════════════════════════════
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "EXPENSE LINE ITEMS",
                bold: true,
                fontSize: 11,
                fillColor: HEADER_BG,
                margin: [5, 3, 5, 3],
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
        },
      },

      // ═══════════════════════════════════════════════════════
      // EXPENSE LINE ITEMS TABLE
      // ═══════════════════════════════════════════════════════
      {
        table: {
          headerRows: 1,
          widths: [22, 55, 70, "*", 55, 50, 45, 60],
          body: expenseTableBody,
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000000",
          vLineColor: () => "#000000",
          paddingLeft: () => 2,
          paddingRight: () => 2,
          paddingTop: () => 1,
          paddingBottom: () => 1,
        },
        margin: [0, 0, 0, 8],
      },

      // ═══════════════════════════════════════════════════════
      // REMARKS & SIGNATURE
      // ═══════════════════════════════════════════════════════
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "REMARKS",
                bold: true,
                fontSize: 11,
                fillColor: HEADER_BG,
                margin: [5, 3, 5, 3],
                border: [false, false, false, false],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
        },
        margin: [0, 0, 0, 2],
      },
      {
        columns: [
          {
            width: "60%",
            stack: [
              {
                text: remarks,
                fontSize: 10,
                margin: [0, 2, 0, 0],
              },
            ],
          },
          {
            width: "40%",
            stack: [
              {
                text: `For,  ${ownerCompanyName}`,
                fontSize: 9,
                bold: true,
                alignment: "right",
                margin: [0, 0, 5, 20],
              },
              {
                text: "AUTHORIZE SIGNATURE",
                bold: true,
                fontSize: 11,
                alignment: "right",
                margin: [0, 15, 5, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 6],
      },
    ],
  };

  return docDefinition;
};

// ──────────────────────────────────────────────────────────────────────────
// MULTI-VOUCHER WRAPPER
// ──────────────────────────────────────────────────────────────────────────
export const generateExpensePDF = (voucherData) => {
  if (!Array.isArray(voucherData)) voucherData = [voucherData];

  const combinedContent = [];
  voucherData.forEach((voucher, idx) => {
    const singleDoc = generateSingleExpensePDF(voucher);
    if (idx > 0) {
      combinedContent.push({ text: "", pageBreak: "before" });
    }
    combinedContent.push(...singleDoc.content);
  });

  const footerVoucher = voucherData[voucherData.length - 1] || {};

  return {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [15, 15, 15, 30],
    defaultStyle: { fontSize: 10, font: "Roboto", color: "#000000" },
    images: { logo: logo },
    footer: function (currentPage, pageCount) {
      return {
        text: `This electronic generated pdf does not require any physical signature. Date : ${formatDateTime(footerVoucher.created_at)}`,
        fontSize: 8,
        alignment: "left",
        margin: [15, 5, 15, 0],
      };
    },
    content: combinedContent,
  };
};

// ──────────────────────────────────────────────────────────────────────────
// EXPORT FUNCTIONS
// ──────────────────────────────────────────────────────────────────────────
export const printSingleExpense = (voucher) => {
  if (!voucher) return alert("No voucher data to print");
  const doc = generateSingleExpensePDF(voucher);
  pdfMake.createPdf(doc).open();
};

export const downloadSingleExpense = (voucher) => {
  if (!voucher) return alert("No voucher data to download");
  const doc = generateSingleExpensePDF(voucher);
  const filename = `ExpenseVoucher_${voucher.expense_id || "Voucher"}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdfMake.createPdf(doc).download(filename);
};

// ──────────────────────────────────────────────────────────────────────────
// FETCH & PRINT — for ExpenseList (list rows only have summary, need details)
// ──────────────────────────────────────────────────────────────────────────
export const fetchAndPrintExpense = async (expenseId) => {
  if (!expenseId) return alert("No voucher ID provided");
  try {
    const response = await getExpenseDetails(expenseId);
    const data = response?.data ?? response;
    if (!data) throw new Error("Empty response");
    printSingleExpense(data);
  } catch (err) {
    console.error("Print expense failed:", err);
    alert("Failed to load voucher details for printing.");
  }
};

export const fetchAndDownloadExpense = async (expenseId) => {
  if (!expenseId) return alert("No voucher ID provided");
  try {
    const response = await getExpenseDetails(expenseId);
    const data = response?.data ?? response;
    if (!data) throw new Error("Empty response");
    downloadSingleExpense(data);
  } catch (err) {
    console.error("Download expense failed:", err);
    alert("Failed to load voucher details for download.");
  }
};